import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Alert,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  SwapHoriz as SwapHorizIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import moment from 'moment';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  maxSchedules: number;
  maxStaff: number;
  planId: string | null;
}

interface PlanSwitchModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlan: any;
  newPlan: any;
  paymentMethod: any;
  isResumingPlan?: boolean;
}

export default function PlanSwitchModal({
  open,
  onClose,
  onConfirm,
  currentPlan,
  newPlan,
  paymentMethod,
  isResumingPlan = false
}: PlanSwitchModalProps) {
  
  const calculatePlanComparison = () => {
    if (!currentPlan?.price || !newPlan?.price) return null;

    const difference = newPlan.price - currentPlan.price;
    const isUpgrade = difference > 0;

    return {
      isUpgrade,
      difference: Math.abs(difference),
      proratedAmount: Math.abs(difference * 0.5) // Simplified proration
    };
  };

  const isSamePlan = currentPlan?.id === newPlan?.id || currentPlan?.planId === newPlan?.planId;
  const comparison = calculatePlanComparison();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.light' }}>
            <SwapHorizIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {isResumingPlan ? 'Resume Plan' : 'Confirm Plan Change'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isResumingPlan ? 'Reactivate your subscription' : 'Review your plan change details'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Current vs New Plan Comparison */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Current Plan
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="text.secondary">
                {currentPlan?.name}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="text.secondary" sx={{ mt: 1 }}>
                ${currentPlan?.price}
                <Typography component="span" variant="caption">
                  /mo
                </Typography>
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50', borderColor: 'primary.main', borderWidth: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                New Plan
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {newPlan?.name}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mt: 1 }}>
                ${newPlan?.price}
                <Typography component="span" variant="caption">
                  /mo
                </Typography>
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Price Change Alert */}
        {comparison && !isResumingPlan && !isSamePlan && (
          <Alert
            severity={comparison.isUpgrade ? "info" : "warning"}
            icon={comparison.isUpgrade ? <TrendingUpIcon /> : <TrendingDownIcon />}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {comparison.isUpgrade ? 'Upgrade' : 'Downgrade'} Summary
            </Typography>
            <Typography variant="body2">
              {comparison.isUpgrade ? 'Additional' : 'Savings'}: <strong>${comparison.difference}/month</strong>
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Prorated charge today: ${comparison.proratedAmount.toFixed(2)}
            </Typography>
          </Alert>
        )}

        {/* Feature Comparison */}
        {!isSamePlan && (
          <>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              What's changing:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={`Schedules: ${currentPlan?.maxSchedules === -1 ? '∞' : currentPlan?.maxSchedules} → ${newPlan?.maxSchedules === -1 ? '∞' : newPlan?.maxSchedules}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={`Staff: ${currentPlan?.maxStaff === -1 ? '∞' : currentPlan?.maxStaff} → ${newPlan?.maxStaff === -1 ? '∞' : newPlan?.maxStaff}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="All features from your new plan"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Important Notes */}
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
            Important:
          </Typography>
          <Typography variant="caption" display="block">
            • Changes take effect immediately
          </Typography>
          <Typography variant="caption" display="block">
            • You'll be charged/credited on a prorated basis
          </Typography>
          <Typography variant="caption" display="block">
            • Your next billing date will be {moment(paymentMethod?.paidThroughDate).add(1, 'day').format('MMMM D, YYYY')}
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button onClick={onConfirm} variant="contained" color="info" sx={{ color: 'white' }}>
          {(isResumingPlan || isSamePlan) ? "Resume Plan" : "Upgrade Plan"}
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}